import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Briefcase, FileCheck, Users, Award } from "lucide-react";
import img from "../../assets/image.jpeg";

/* ================= DATA ================= */

const stats = [
  { value: 500, suffix: "+", label: "Counsellings", icon: Briefcase },
  { value: 200, suffix: "+", label: "Visas", icon: FileCheck },
  { value: 1000, suffix: "+", label: "Clients", icon: Users },
  { value: 100, suffix: "%", label: "Student Satisfied", icon: Award },
];

/* ================= ANIMATION ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.25 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ================= COUNT HOOK ================= */

function useCountUp(end, start) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let current = 0;
    const duration = 2800;
    const frameRate = 30;
    const totalFrames = duration / frameRate;
    const increment = end / totalFrames;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        current = end;
        clearInterval(timer);
      }
      setCount(Math.floor(current));
    }, frameRate);

    return () => clearInterval(timer);
  }, [end, start]);

  return count;
}

/* ================= STAT ITEM ================= */

function StatItem({ item, isInView }) {
  const count = useCountUp(item.value, isInView);
  const Icon = item.icon;

  return (
    <motion.div
      variants={cardVariants}
      className="
        relative
        flex flex-col items-center justify-center
        py-10 px-4 text-center
        
        bg-gradient-to-br from-blue-500/90 via-blue-600/90 to-blue-800/90
        backdrop-blur-xl
        border border-white/25
        rounded-2xl
        
        transition-all duration-300
        hover:scale-[1.04]
        hover:shadow-[0_12px_40px_rgba(59,130,246,0.45)]
      "
    >
      {/* Shine */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/15 to-transparent pointer-events-none" />

      {/* ICON */}
      <div className="relative w-14 h-14 rounded-full bg-white/25 flex items-center justify-center mb-4 border border-white/30">
        <Icon size={24} className="text-white" />
      </div>

      {/* NUMBER */}
      <h3 className="relative text-white text-3xl md:text-4xl font-semibold">
        {count}{item.suffix}
      </h3>

      {/* LABEL */}
      <p className="relative mt-1 text-sm text-white/90 tracking-wide">
        {item.label}
      </p>
    </motion.div>
  );
}


/* ================= MAIN ================= */

export default function StatsCounter() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
   <section ref={ref} className="relative w-full py-24 md:py-32 overflow-hidden">

  {/* 1️⃣ SOLID BRAND BLUE BASE */}
  <div className="absolute inset-0 bg-[#0A2A8A]" />

  {/* 2️⃣ BACKGROUND IMAGE */}
  <img
    src={img}
    alt="Background"
    className="absolute inset-0 w-full h-full object-cover opacity-99"
  />

  {/* 3️⃣ BLUE GRADIENT OVERLAY (VISIBLE & PREMIUM) */}
  <div className="absolute inset-0 bg-gradient-to-br from-blue-300/85 via-blue-300/80 to-blue-350/90" />

  {/* 4️⃣ CONTENT */}
  <motion.div
    variants={containerVariants}
    initial="hidden"
    animate={isInView ? "visible" : "hidden"}
    className="
      relative z-10
      max-w-[1400px]
      mx-auto
      grid grid-cols-2 md:grid-cols-4
      gap-y-10 gap-x-10
      px-6
    "
  >
    {stats.map((item, index) => (
      <StatItem key={index} item={item} isInView={isInView} />
    ))}
  </motion.div>

</section>

  );
}
